import Feather from '@expo/vector-icons/Feather';
import { CustomFieldValuesModel } from '@w3lcome/types';
import { Text, Spin, Header, InfoCard } from '_/components';
import ContainerWidthLimit from '_/components/ContainerWidthLimit';
import React from 'react';
import {
  View,
  Platform,
  UIManager,
  ScrollView,
  TouchableOpacity,
  Text as RNText,
} from 'react-native';
import themes from '_/constants/themes';

import { DropdownList } from './DropdownList/DropdownList';
import ExternalGuests from './ExternalGuests';
import Guest from './GuestList/Guest';
import InviteeList from './InviteeList';
import LinkList from './LinkList';
import MenuModal from './MenuModal';
import styles from './styles';
import { useMeeting } from './useMeeting';
import GuestList from './GuestList';
import { useTranslation } from 'react-i18next';

if (Platform.OS === 'android' && UIManager.setLayoutAnimationEnabledExperimental) {
  UIManager.setLayoutAnimationEnabledExperimental(true);
}

const Meeting: React.FC = () => {
  const {
    visits,
    loading,
    hostName,
    menuModalVisible,
    hideModal,
    meeting,
    expanded,
    toggleExpanded,
    showModal,
    handleBack,
    headerTitle,
    subjectTitle,
    startDateTitle,
    endDateTitle,
    editMeetingHostTitle,
    meetingVisitFlowTypeTitle,
    editMeetingLocationTitle,
    editMeetingNotesTitle,
    editMeetingDescriptionTitle,
    meetingHideInfo,
    meetingShoweInfo,
    meetingCoHostsTitle,
    isThereAnyCoHost,
    coHostsData,
    startDateValue,
    endDateValue,
    language,
    visitFlowTypes,
    isCredentialing,
    addOrUpdateCredentialing,
  } = useMeeting();

  const { i18n } = useTranslation();

  const getVisitFlowTypeOnVisit = () =>
    visitFlowTypes.find((flowType) => flowType.id === meeting?.visitFlowTypeId);

  const confirmationMessage =
    meeting?.meetingType?.confirmationMessage?.[i18n.language] ||
    meeting?.meetingType?.confirmationMessage?.['pt-BR'] ||
    meeting?.['meetingType.confirmationMessage']?.[i18n.language] ||
    meeting?.['meetingType.confirmationMessage']?.['pt-BR'];

  function renderBoldText(text: string) {
    const parts = text.split(/\*\*(.*?)\*\*/g);
    return (
      <RNText style={{ marginTop: 12, color: '#555' }}>
        {parts.map((part, index) =>
          index % 2 === 1 ? (
            <RNText key={index} style={{ fontWeight: 'bold' }}>
              {part}
            </RNText>
          ) : (
            <RNText key={index}>{part}</RNText>
          )
        )}
      </RNText>
    );
  }
  return (
    <ContainerWidthLimit>
      <View style={styles.container}>
        <Spin visible={loading} />
        <Header
          backgroundColor={themes.colors.white}
          handleBack={handleBack}
          title={headerTitle}
          rightItem={
            meeting?.end && new Date(meeting.end) > new Date() ? (
              <TouchableOpacity
                hitSlop={{ top: 30, left: 30, bottom: 30, right: 30 }}
                onPress={showModal}
              >
                <Feather name="more-vertical" style={styles.iconMoreVertical} />
              </TouchableOpacity>
            ) : undefined
          }
        />
        <ScrollView
          style={styles.content}
          contentContainerStyle={{ paddingBottom: 48 }}
          showsHorizontalScrollIndicator={false}
        >
          <InfoCard copy title={subjectTitle ?? ''} value={meeting?.title} />
          <InfoCard copy title={startDateTitle ?? ''} value={startDateValue} />
          <InfoCard copy title={endDateTitle ?? ''} value={endDateValue} />
          {meeting?.customFieldValues &&
            meeting.customFieldValues.map((value: CustomFieldValuesModel) => (
              <InfoCard
                key={value.id}
                copy
                title={
                  (value.customField && value.customField?.placeholder?.[language]) ||
                  value.customField?.name ||
                  '-'
                }
                value={value.localizedValue?.[i18n.language] || value.value}
              />
            ))}
          <InfoCard copy title={editMeetingHostTitle} value={hostName} />

          {visitFlowTypes.length > 1 && (
            <InfoCard
              copy
              title={meetingVisitFlowTypeTitle}
              value={meeting?.visitFlowType?.name || getVisitFlowTypeOnVisit()?.name}
            />
          )}

          <InfoCard
            copy
            title={editMeetingLocationTitle}
            value={meeting?.meetingLocation?.name || meeting?.location}
            img={meeting?.meetingLocation?.pictureUrl}
          />

          <LinkList meeting={meeting} />

          {expanded && (
            <View>
              <InfoCard copy title={editMeetingNotesTitle} value={meeting?.notes} />
              <InfoCard copy title={editMeetingDescriptionTitle} value={meeting?.message} />
              <ExternalGuests visits={visits} />
            </View>
          )}

          {(!!meeting?.notes || !!meeting?.description || !!meeting?.visits) && (
            <TouchableOpacity onPress={toggleExpanded} style={styles.expandButtonContainer}>
              <Text style={styles.expandButtonText}>
                {expanded ? meetingHideInfo : meetingShoweInfo}
              </Text>
              <Feather style={styles.icon} name={expanded ? 'chevron-up' : 'chevron-down'} />
            </TouchableOpacity>
          )}

          {meeting && <GuestList meeting={meeting} isCredentialing={isCredentialing} />}

          {isThereAnyCoHost && (
            <DropdownList title={meetingCoHostsTitle}>
              {coHostsData.map((email) => (
                <Guest key={email} guest={{ email }} />
              ))}
            </DropdownList>
          )}

          {!isCredentialing && <InviteeList meeting={meeting} />}

          {confirmationMessage && renderBoldText(confirmationMessage)}
        </ScrollView>
        <MenuModal
          visible={menuModalVisible}
          hideModal={hideModal}
          isCredentialing={isCredentialing}
          onChange={addOrUpdateCredentialing}
        />
      </View>
    </ContainerWidthLimit>
  );
};

export default Meeting;
